import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { CheckCircle, AlertCircle, Loader2, User, Building2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTenancyCreation } from '@/hooks/useTenancyCreation';
import { OnboardingFormData } from '@/services/tenancyCreatorService';

type TenancySetupFixField = 'website' | 'email' | 'framework' | 'nameOnCard' | 'account' | 'business' | 'billing';

interface TenancySetupFixRequest {
  field: TenancySetupFixField;
  message: string;
  currentValue: string;
}

/**
 * Phase of the setup process
 * - 'account': Creating the user account (Firebase auth)
 * - 'tenancy': Creating the tenancy/workspace
 */
export type SetupPhase = 'account' | 'tenancy';

interface TenancyCreationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  formData: OnboardingFormData;
  onComplete: (customerID?: string) => void;
  onFixRequested?: (details: TenancySetupFixRequest) => void;
  /** Current phase of setup - 'account' for user creation, 'tenancy' for workspace setup */
  phase?: SetupPhase;
  /** Progress percentage for account creation phase (0-100) */
  accountProgress?: number;
  /** Error message from account creation phase */
  accountError?: string | null;
}

export const TenancyCreationDialog: React.FC<TenancyCreationDialogProps> = ({
  isOpen,
  onClose,
  formData,
  onComplete,
  onFixRequested,
  phase = 'tenancy',
  accountProgress = 0,
  accountError = null
}) => {
  const {
    state,
    isProcessing,
    isSuccess,
    isError,
    progressPercentage,
    currentProgress,
    result,
    error,
    createTenancy,
    reset
  } = useTenancyCreation();

  // Only start tenancy creation when in tenancy phase (account creation is complete)
  React.useEffect(() => {
    if (isOpen && state === 'idle' && phase === 'tenancy') {
      createTenancy(formData);
    }
  }, [isOpen, state, phase, createTenancy, formData]);

  const handleContinue = () => {
    if (isSuccess && result?.customerID) {
      onComplete(result.customerID);
    } else if (isError) {
      onClose();
    }
  };

  const getFixableErrorDetails = (): TenancySetupFixRequest | null => {
    if (!error) return null;
    const lower = error.toLowerCase();
    const cleanedMessage = error.replace(/^validation failed:\s*/i, '');
    
    // Website errors
    if (lower.includes('website')) {
      return {
        field: 'website',
        message: cleanedMessage || error,
        currentValue: formData.website,
      };
    }
    
    // Email errors (including "already in use")
    if (lower.includes('email')) {
      return {
        field: 'email',
        message: cleanedMessage || error,
        currentValue: formData.email,
      };
    }
    
    // Framework selection errors
    if (lower.includes('framework')) {
      return {
        field: 'framework',
        message: cleanedMessage || error,
        currentValue: formData.selectedFrameworks?.join(', ') || '',
      };
    }
    
    // Name on card errors
    if (lower.includes('name on card')) {
      return {
        field: 'nameOnCard',
        message: cleanedMessage || error,
        currentValue: formData.nameOnCard || '',
      };
    }
    
    // Card/payment errors
    if (lower.includes('card') || lower.includes('payment')) {
      return {
        field: 'billing',
        message: cleanedMessage || error,
        currentValue: '',
      };
    }
    
    // Name/password errors -> account step
    if (lower.includes('name is required') || lower.includes('password')) {
      return {
        field: 'account',
        message: cleanedMessage || error,
        currentValue: '',
      };
    }
    
    // Company/team size errors -> business step
    if (lower.includes('company') || lower.includes('team size')) {
      return {
        field: 'business',
        message: cleanedMessage || error,
        currentValue: '',
      };
    }
    
    // For unknown errors, return null so the dialog can retry without navigating away
    // This prevents the "Try Again" button from being unresponsive for non-fixable errors
    return null;
  };

  const handleTryAgain = () => {
    const fixDetails = getFixableErrorDetails();
    if (fixDetails && onFixRequested) {
      reset();
      onFixRequested(fixDetails);
      onClose();
      return;
    }
    reset();
    createTenancy(formData);
  };

  React.useEffect(() => {
    if (!isOpen) {
      reset();
    }
  }, [isOpen, reset]);

  const getStageMessage = () => {
    if (!currentProgress) return 'Initializing...';
    
    const stageMessages = {
      'validation': 'Validating request...',
      'existence-check': 'Checking account status...',
      'data-retrieval': 'Retrieving onboarding data...',
      'template-verification': 'Verifying templates...',
      'customer-creation': 'Creating customer document...',
      'template-copy': 'Setting up your workspace...',
      'completion': 'Tenancy creation completed!'
    };
    
    return stageMessages[currentProgress.stage as keyof typeof stageMessages] || currentProgress.message;
  };

  // Determine overall state considering both phases
  const isAccountPhase = phase === 'account';
  const isTenancyPhase = phase === 'tenancy';
  const showAccountError = isAccountPhase && accountError;
  const showTenancyError = isTenancyPhase && isError;
  const isAnyError = showAccountError || showTenancyError;
  const isAccountComplete = isTenancyPhase; // If we're in tenancy phase, account is done
  const isTenancyComplete = isTenancyPhase && isSuccess;
  const isAllComplete = isTenancyComplete;

  // Get the title based on current phase and state
  const getDialogTitle = () => {
    if (isAllComplete) return 'Account Created Successfully!';
    if (isAnyError) return 'Setup Error';
    if (isAccountPhase) return 'Creating Your Account';
    return 'Setting Up Your Workspace';
  };

  // Get the title icon
  const getTitleIcon = () => {
    if (isAllComplete) return <CheckCircle className="h-5 w-5 text-green-500" />;
    if (isAnyError) return <AlertCircle className="h-5 w-5 text-red-500" />;
    return <Loader2 className="h-5 w-5 animate-spin" />;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-[500px] max-w-[95vw] bg-card" onPointerDownOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {getTitleIcon()}
            {getDialogTitle()}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Two-step progress indicator */}
          {!isAnyError && !isAllComplete && (
            <div className="relative flex items-start justify-between mb-4 px-8">
              {/* Connector line - positioned behind icons */}
              <div 
                className={`absolute top-5 left-1/2 -translate-x-1/2 w-[calc(100%-8rem)] h-0.5 ${
                  isAccountComplete ? 'bg-green-500' : 'bg-muted-foreground/20'
                }`} 
              />
              
              {/* Step 1: Account Creation */}
              <div className="flex flex-col items-center gap-2 z-10">
                <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 transition-colors ${
                  isAccountComplete 
                    ? 'bg-green-500 border-green-500 text-white' 
                    : isAccountPhase 
                      ? 'border-primary bg-primary text-primary-foreground' 
                      : 'border-muted-foreground/30 bg-background text-muted-foreground'
                }`}>
                  {isAccountComplete ? (
                    <CheckCircle className="h-5 w-5" />
                  ) : (
                    <User className="h-5 w-5" />
                  )}
                </div>
                <span className={`text-xs font-medium ${
                  isAccountComplete 
                    ? 'text-green-600 dark:text-green-400' 
                    : isAccountPhase 
                      ? 'text-foreground' 
                      : 'text-muted-foreground'
                }`}>
                  Account
                </span>
              </div>

              {/* Step 2: Tenancy Creation */}
              <div className="flex flex-col items-center gap-2 z-10">
                <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 transition-colors ${
                  isTenancyComplete 
                    ? 'bg-green-500 border-green-500 text-white' 
                    : isTenancyPhase 
                      ? 'border-primary bg-primary text-primary-foreground' 
                      : 'border-muted-foreground/30 bg-background text-muted-foreground'
                }`}>
                  {isTenancyComplete ? (
                    <CheckCircle className="h-5 w-5" />
                  ) : (
                    <Building2 className="h-5 w-5" />
                  )}
                </div>
                <span className={`text-xs font-medium ${
                  isTenancyComplete 
                    ? 'text-green-600 dark:text-green-400' 
                    : isTenancyPhase 
                      ? 'text-foreground' 
                      : 'text-muted-foreground'
                }`}>
                  Workspace
                </span>
              </div>
            </div>
          )}

          {/* Account Creation Progress */}
          {isAccountPhase && !accountError && (
            <>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Creating your account...</span>
                  <span>{accountProgress}%</span>
                </div>
                <Progress value={accountProgress} className="w-full" />
              </div>
              
              <div className="text-sm text-muted-foreground">
                Please wait while we set up your account.
              </div>
            </>
          )}

          {/* Tenancy Creation Progress */}
          {isTenancyPhase && isProcessing && (
            <>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>{getStageMessage()}</span>
                  <span>{progressPercentage}%</span>
                </div>
                <Progress value={progressPercentage} className="w-full" />
              </div>
              
              <div className="text-sm text-muted-foreground">
                This may take a few minutes. Please don't close this window.
              </div>
            </>
          )}

          {/* DED-1582: Center the success message text */}
          {isAllComplete && (
            <div className="border border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950 rounded-lg px-4 py-3 flex flex-col items-center gap-2 text-center">
              {/* <CheckCircle className="h-5 w-5 text-green-500" /> */}
              <p className="text-sm text-green-700 dark:text-green-300 max-w-xs">
                Your de.iterate account has been successfully created! You can now log in and start building your compliance program.
              </p>
            </div>
          )}

          {/* Account creation error */}
          {showAccountError && (
            <div className="border border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950 rounded-lg p-4 max-w-full overflow-hidden">
              <div className="flex gap-3 items-start max-w-full overflow-hidden">
                <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
                <div 
                  className="text-red-700 dark:text-red-300 text-sm leading-relaxed max-w-full overflow-hidden"
                  style={{
                    wordBreak: 'break-word',
                    overflowWrap: 'break-word',
                    width: '400px',
                    maxWidth: '400px'
                  }}
                >
                  {accountError || 'An error occurred while creating your account. Please try again or contact support.'}
                </div>
              </div>
            </div>
          )}

          {/* Tenancy creation error */}
          {showTenancyError && (
            <div className="border border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950 rounded-lg p-4 max-w-full overflow-hidden">
              <div className="flex gap-3 items-start max-w-full overflow-hidden">
                <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
                <div 
                  className="text-red-700 dark:text-red-300 text-sm leading-relaxed max-w-full overflow-hidden"
                  style={{
                    wordBreak: 'break-word',
                    overflowWrap: 'break-word',
                    width: '400px',
                    maxWidth: '400px'
                  }}
                >
                  {error || 'An error occurred while setting up your workspace. Please try again or contact support.'}
                </div>
              </div>
            </div>
          )}

          {(isAllComplete || isAnyError) && (
            <div className="flex justify-end gap-2">
              {isAnyError && (
                <Button variant="outline" onClick={onClose}>
                  Close
                </Button>
              )}
              <Button onClick={isAllComplete ? handleContinue : handleTryAgain}>
                {isAllComplete ? 'Continue to Login' : 'Try Again'}
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
